import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp,
  addDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { Heading } from "./heading";
import { Text } from "./text";
import { Button } from "./button";
import { DateTime } from "luxon";
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogActions,
  DialogBody,
  DialogTextarea,
} from "./dialog";
import { showAlert } from "../alertService";

const ONBOARDING_STATUS = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  DONE: "done",
};

const LEAD_STATUS = {
  NEW: "new",
  CONTACTED: "contacted",
  NEGOTIATING: "negotiating",
  CONVERTED: "converted",
  LOST: "lost",
};

const INVITATION_STATUS = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  EXPIRED: "expired",
};

const ITEMS_PER_PAGE = 10;

const Home = () => {
  // Venues State
  const [venues, setVenues] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("lastUpdate");
  const [sortDirection, setSortDirection] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    isActive: false,
    isPayed: false,
    amount: "",
    onboardingStatus: ONBOARDING_STATUS.NOT_STARTED,
    premiumCotizaciones: false,
    telegram: false,
    multiespacio: false,
  });
  const [updating, setUpdating] = useState(false);

  // Leads State
  const [leads, setLeads] = useState([]);
  const [isLeadDialogOpen, setIsLeadDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadForm, setLeadForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    status: LEAD_STATUS.NEW,
    notes: "",
    createdAt: null,
    lastUpdate: null,
  });

  // Invitations State
  const [invitations, setInvitations] = useState([]);
  const [isInvitationDialogOpen, setIsInvitationDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [invitationForm, setInvitationForm] = useState({
    venueName: "",
    contactName: "",
    email: "",
    phone: "",
    status: INVITATION_STATUS.PENDING,
    sentAt: null,
    expiresAt: null,
  });

  // Add new state for invitation filter
  const [invitationStatusFilter, setInvitationStatusFilter] = useState("all");

  // Add new state for lead filters
  const [leadStatusFilter, setLeadStatusFilter] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch venues
        const venuesSnapshot = await getDocs(collection(db, "venues"));
        const venuesRaw = venuesSnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        // Fetch adminConfig/features for each venue (premium flags)
        const venuesData = await Promise.all(
          venuesRaw.map(async (v) => {
            try {
              const featSnap = await getDoc(doc(db, "venues", v.id, "adminConfig", "features"));
              const feat = featSnap.exists() ? featSnap.data() : {};
              return {
                ...v,
                _premiumCotizaciones: feat.premiumCotizaciones || false,
                _multiespacio: feat.multiespacio || false,
              };
            } catch {
              return { ...v, _premiumCotizaciones: false, _multiespacio: false };
            }
          })
        );
        setVenues(venuesData);

        // Fetch leads
        const leadsSnapshot = await getDocs(collection(db, "leads"));
        const leadsData = leadsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLeads(leadsData);

        // Fetch invitations
        const invitationsSnapshot = await getDocs(
          collection(db, "invitations")
        );
        const invitationsData = invitationsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setInvitations(invitationsData);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Error al cargar los datos");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleRowClick = async (venue) => {
    setSelectedVenue(venue);
    // Fetch admin-managed features from subcollection
    let premiumCotizaciones = false;
    let telegram = false;
    let multiespacio = false;
    try {
      const featuresSnap = await getDoc(doc(db, "venues", venue.id, "adminConfig", "features"));
      if (featuresSnap.exists()) {
        premiumCotizaciones = featuresSnap.data().premiumCotizaciones || false;
        telegram = featuresSnap.data().telegram || false;
        multiespacio = featuresSnap.data().multiespacio || false;
      }
    } catch (err) {
      console.warn("Failed to fetch venue features:", err);
    }
    setEditForm({
      isActive: venue.isActive || false,
      isActiveAdmin: venue.isActiveAdmin || false,
      isPayed: venue.isPayed || false,
      amount: venue.amount || "",
      onboardingStatus: venue.onboardingStatus || ONBOARDING_STATUS.NOT_STARTED,
      premiumCotizaciones,
      telegram,
      multiespacio,
    });
    setIsDialogOpen(true);
  };

  const handleSaveChanges = async () => {
    if (!selectedVenue) return;
    setUpdating(true);
    try {
      const venueRef = doc(db, "venues", selectedVenue.id);
      // Separate premiumCotizaciones from venue-level fields
      const { premiumCotizaciones, telegram, multiespacio, ...venueFields } = editForm;
      const updateData = {
        ...venueFields,
        // Copia denormalizada del flag en el doc del venue: el backend la lee
        // desde ahí para no pagar una lectura de adminConfig por venue en el
        // listado y en availability. La fuente de verdad sigue siendo
        // adminConfig/features (las rules solo dejan que un admin la mueva).
        multiespacio,
        lastUpdate: Timestamp.now(),
      };
      await updateDoc(venueRef, updateData);

      // Save premium flag to adminConfig subcollection
      const featuresRef = doc(db, "venues", selectedVenue.id, "adminConfig", "features");
      await setDoc(featuresRef, { premiumCotizaciones, telegram, multiespacio }, { merge: true });

      // Update local state
      setVenues(
        venues.map((venue) =>
          venue.id === selectedVenue.id
            ? {
                ...venue,
                ...updateData,
                lastUpdate: updateData.lastUpdate,
                _premiumCotizaciones: premiumCotizaciones,
                _multiespacio: multiespacio,
              }
            : venue
        )
      );

      setIsDialogOpen(false);
      showAlert("Venue actualizado exitosamente", "success");
    } catch (error) {
      console.error("Error updating venue:", error);
      showAlert("Error al actualizar el venue", "error");
    } finally {
      setUpdating(false);
    }
  };

  const getOnboardingStatusDisplay = (status) => {
    switch (status) {
      case ONBOARDING_STATUS.IN_PROGRESS:
        return {
          text: "En Proceso",
          classes: "bg-blue-100 text-blue-800",
        };
      case ONBOARDING_STATUS.DONE:
        return {
          text: "Completado",
          classes: "bg-green-100 text-green-800",
        };
      default:
        return {
          text: "No Iniciado",
          classes: "bg-gray-100 text-gray-800",
        };
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      // Handle both Firestore Timestamp and JavaScript Date objects
      const date =
        timestamp instanceof Timestamp
          ? timestamp.toDate()
          : new Date(timestamp.seconds * 1000);

      return DateTime.fromJSDate(date)
        .setLocale("es")
        .toFormat("dd/MM/yyyy HH:mm");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };

  const filteredAndSortedVenues = useMemo(() => {
    let filtered = [...venues];

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((venue) => {
        if (statusFilter === "active") return venue.isActive;
        if (statusFilter === "inactive") return !venue.isActive;
        if (statusFilter === "paid") return venue.isPayed;
        if (statusFilter === "pending") return !venue.isPayed;
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === "name") {
        return sortDirection === "asc"
          ? a.name?.localeCompare(b.name || "")
          : b.name?.localeCompare(a.name || "");
      }
      if (sortBy === "lastUpdate") {
        const aTime = a.lastUpdate?.seconds || 0;
        const bTime = b.lastUpdate?.seconds || 0;
        return sortDirection === "asc" ? aTime - bTime : bTime - aTime;
      }
      if (sortBy === "amount") {
        const aAmount = a.amount || 0;
        const bAmount = b.amount || 0;
        return sortDirection === "asc" ? aAmount - bAmount : bAmount - aAmount;
      }
      return 0;
    });

    return filtered;
  }, [venues, sortBy, sortDirection, statusFilter]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDirection("desc");
    }
  };

  // Leads functions
  const handleCreateLead = async () => {
    try {
      const now = Timestamp.now();
      const newLead = {
        ...leadForm,
        createdAt: now,
        lastUpdate: now,
      };

      const docRef = await addDoc(collection(db, "leads"), newLead);
      setLeads([...leads, { id: docRef.id, ...newLead }]);
      setIsLeadDialogOpen(false);
      setLeadForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        status: LEAD_STATUS.NEW,
        notes: "",
        createdAt: null,
        lastUpdate: null,
      });
      showAlert("Lead creado exitosamente", "success");
    } catch (error) {
      console.error("Error creating lead:", error);
      showAlert("Error al crear el lead", "error");
    }
  };

  const handleUpdateLead = async () => {
    if (!selectedLead) return;
    try {
      const leadRef = doc(db, "leads", selectedLead.id);
      const updateData = {
        ...leadForm,
        lastUpdate: Timestamp.now(),
      };
      await updateDoc(leadRef, updateData);

      setLeads(
        leads.map((lead) =>
          lead.id === selectedLead.id ? { ...lead, ...updateData } : lead
        )
      );

      setIsLeadDialogOpen(false);
      showAlert("Lead actualizado exitosamente", "success");
    } catch (error) {
      console.error("Error updating lead:", error);
      showAlert("Error al actualizar el lead", "error");
    }
  };

  // Invitations functions
  const handleCreateInvitation = async () => {
    try {
      const now = Timestamp.now();
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 7); // 7 days expiration

      const newInvitation = {
        ...invitationForm,
        sentAt: now,
        expiresAt: Timestamp.fromDate(expirationDate),
        status: INVITATION_STATUS.PENDING,
      };

      const docRef = await addDoc(collection(db, "invitations"), newInvitation);
      setInvitations([...invitations, { id: docRef.id, ...newInvitation }]);
      setIsInvitationDialogOpen(false);
      setInvitationForm({
        venueName: "",
        contactName: "",
        email: "",
        phone: "",
        status: INVITATION_STATUS.PENDING,
        sentAt: null,
        expiresAt: null,
      });
      showAlert("Invitación creada exitosamente", "success");
    } catch (error) {
      console.error("Error creating invitation:", error);
      showAlert("Error al crear la invitación", "error");
    }
  };

  const handleUpdateInvitation = async () => {
    if (!selectedInvitation) return;
    try {
      const invitationRef = doc(db, "invitations", selectedInvitation.id);
      await updateDoc(invitationRef, invitationForm);

      setInvitations(
        invitations.map((invitation) =>
          invitation.id === selectedInvitation.id
            ? { ...invitation, ...invitationForm }
            : invitation
        )
      );

      setIsInvitationDialogOpen(false);
      showAlert("Invitación actualizada exitosamente", "success");
    } catch (error) {
      console.error("Error updating invitation:", error);
      showAlert("Error al actualizar la invitación", "error");
    }
  };

  // Add filtered invitations
  const filteredInvitations = useMemo(() => {
    if (invitationStatusFilter === "all") return invitations;
    return invitations.filter(
      (invitation) => invitation.status === invitationStatusFilter
    );
  }, [invitations, invitationStatusFilter]);

  // Add filtered leads logic
  const filteredLeads = useMemo(() => {
    let filtered = [...leads];

    // Apply status filter
    if (leadStatusFilter !== "all") {
      filtered = filtered.filter((lead) => lead.status === leadStatusFilter);
    }

    return filtered;
  }, [leads, leadStatusFilter]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredAndSortedVenues.length / ITEMS_PER_PAGE);
  const paginatedVenues = filteredAndSortedVenues.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (loading) {
    return <div className="p-4">Cargando...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-600">{error}</div>;
  }

  return (
    <div className="container mx-auto p-4 space-y-8 max-lg:px-0">
      {/* Venues Section */}
      <section className="space-y-6">
        <Heading level={2}>Resumen de Venues</Heading>

        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <select
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
              <option value="paid">Pagados</option>
              <option value="pending">Pendientes</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto ring-1 ring-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("name")}
                >
                  Nombre del Venue{" "}
                  {sortBy === "name" && (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado Admin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado de Pago
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("amount")}
                >
                  Monto{" "}
                  {sortBy === "amount" && (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado de Onboarding
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PDF Premium
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Multiespacio
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("lastUpdate")}
                >
                  Última Actualización{" "}
                  {sortBy === "lastUpdate" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedVenues.map((venue) => (
                <tr
                  key={venue.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(venue)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Text className="text-sm font-medium text-gray-900">
                      {venue.name}
                    </Text>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        venue.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {venue.isActive ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        venue.isActiveAdmin
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {venue.isActiveAdmin ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        venue.isPayed
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {venue.isPayed ? "Pagado" : "Pendiente"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {venue.amount
                      ? new Intl.NumberFormat("es-MX", {
                          style: "currency",
                          currency: "MXN",
                        }).format(venue.amount)
                      : "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        getOnboardingStatusDisplay(venue.onboardingStatus)
                          .classes
                      }`}
                    >
                      {getOnboardingStatusDisplay(venue.onboardingStatus).text}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        venue._premiumCotizaciones
                          ? "bg-purple-100 text-purple-800"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {venue._premiumCotizaciones ? "Premium" : "Free"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {/* Apagado ⇒ el venue puede capturar espacios pero solo
                        de exhibición: no se reservan por separado. */}
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        venue._multiespacio
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {venue._multiespacio ? "Activo" : "Exhibición"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(venue.lastUpdate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-500">
            Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1} a{" "}
            {Math.min(
              currentPage * ITEMS_PER_PAGE,
              filteredAndSortedVenues.length
            )}{" "}
            de {filteredAndSortedVenues.length} venues
          </div>
          <div className="flex gap-2">
            <Button
              outline
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            <Button
              outline
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </section>

      {/* Leads Section */}
      <section className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Heading level={2}>Leads</Heading>
            <select
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              value={leadStatusFilter}
              onChange={(e) => setLeadStatusFilter(e.target.value)}
            >
              <option value="all">Todos los estados</option>
              <option value={LEAD_STATUS.NEW}>Nuevos</option>
              <option value={LEAD_STATUS.CONTACTED}>Contactados</option>
              <option value={LEAD_STATUS.NEGOTIATING}>En Negociación</option>
              <option value={LEAD_STATUS.CONVERTED}>Convertidos</option>
              <option value={LEAD_STATUS.LOST}>Perdidos</option>
            </select>
          </div>
          <Button
            onClick={() => {
              setSelectedLead(null);
              setLeadForm({
                name: "",
                email: "",
                phone: "",
                company: "",
                status: LEAD_STATUS.NEW,
                notes: "",
                createdAt: null,
                lastUpdate: null,
              });
              setIsLeadDialogOpen(true);
            }}
          >
            Nuevo Lead
          </Button>
        </div>

        <div className="overflow-x-auto ring-1 ring-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contacto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Última Actualización
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLeads.map((lead) => (
                <tr
                  key={lead.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedLead(lead);
                    setLeadForm({
                      name: lead.name,
                      email: lead.email,
                      phone: lead.phone,
                      company: lead.company,
                      status: lead.status,
                      notes: lead.notes,
                    });
                    setIsLeadDialogOpen(true);
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Text className="text-sm font-medium text-gray-900">
                      {lead.name}
                    </Text>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Text className="text-sm text-gray-500">
                      {lead.company}
                    </Text>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      <div>{lead.email}</div>
                      <div>{lead.phone}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        lead.status === LEAD_STATUS.NEW
                          ? "bg-blue-100 text-blue-800"
                          : lead.status === LEAD_STATUS.CONTACTED
                          ? "bg-yellow-100 text-yellow-800"
                          : lead.status === LEAD_STATUS.NEGOTIATING
                          ? "bg-purple-100 text-purple-800"
                          : lead.status === LEAD_STATUS.CONVERTED
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {lead.status === LEAD_STATUS.NEW
                        ? "Nuevo"
                        : lead.status === LEAD_STATUS.CONTACTED
                        ? "Contactado"
                        : lead.status === LEAD_STATUS.NEGOTIATING
                        ? "En Negociación"
                        : lead.status === LEAD_STATUS.CONVERTED
                        ? "Convertido"
                        : "Perdido"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(lead.lastUpdate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-sm text-gray-500">
          Total: {filteredLeads.length} leads
          {leadStatusFilter !== "all" &&
            ` (Filtrados por estado: ${
              leadStatusFilter === LEAD_STATUS.NEW
                ? "Nuevos"
                : leadStatusFilter === LEAD_STATUS.CONTACTED
                ? "Contactados"
                : leadStatusFilter === LEAD_STATUS.NEGOTIATING
                ? "En Negociación"
                : leadStatusFilter === LEAD_STATUS.CONVERTED
                ? "Convertidos"
                : "Perdidos"
            })`}
        </div>
      </section>

      {/* Venue Invitations Section */}
      <section className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Heading level={2}>Invitaciones a Venues</Heading>
            <select
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              value={invitationStatusFilter}
              onChange={(e) => setInvitationStatusFilter(e.target.value)}
            >
              <option value="all">Todos los estados</option>
              <option value={INVITATION_STATUS.PENDING}>Pendientes</option>
              <option value={INVITATION_STATUS.ACCEPTED}>Aceptadas</option>
              <option value={INVITATION_STATUS.REJECTED}>Rechazadas</option>
              <option value={INVITATION_STATUS.EXPIRED}>Expiradas</option>
            </select>
          </div>
          <Button
            onClick={() => {
              setSelectedInvitation(null);
              setInvitationForm({
                venueName: "",
                contactName: "",
                email: "",
                phone: "",
                status: INVITATION_STATUS.PENDING,
                sentAt: null,
                expiresAt: null,
              });
              setIsInvitationDialogOpen(true);
            }}
          >
            Nueva Invitación
          </Button>
        </div>

        <div className="overflow-x-auto ring-1 ring-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Venue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contacto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha de Envío
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expira
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvitations.map((invitation) => (
                <tr
                  key={invitation.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedInvitation(invitation);
                    setInvitationForm({
                      venueName: invitation.venueName,
                      contactName: invitation.contactName,
                      email: invitation.email,
                      phone: invitation.phone,
                      status: invitation.status,
                    });
                    setIsInvitationDialogOpen(true);
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Text className="text-sm font-medium text-gray-900">
                      {invitation.venueName}
                    </Text>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      <div>{invitation.contactName}</div>
                      <div>{invitation.email}</div>
                      <div>{invitation.phone}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        invitation.status === INVITATION_STATUS.PENDING
                          ? "bg-yellow-100 text-yellow-800"
                          : invitation.status === INVITATION_STATUS.ACCEPTED
                          ? "bg-green-100 text-green-800"
                          : invitation.status === INVITATION_STATUS.REJECTED
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {invitation.status === INVITATION_STATUS.PENDING
                        ? "Pendiente"
                        : invitation.status === INVITATION_STATUS.ACCEPTED
                        ? "Aceptada"
                        : invitation.status === INVITATION_STATUS.REJECTED
                        ? "Rechazada"
                        : "Expirada"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invitation.sentAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invitation.expiresAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-sm text-gray-500">
          Total: {filteredInvitations.length} invitaciones
          {invitationStatusFilter !== "all" &&
            ` (Filtradas por estado: ${
              invitationStatusFilter === INVITATION_STATUS.PENDING
                ? "Pendientes"
                : invitationStatusFilter === INVITATION_STATUS.ACCEPTED
                ? "Aceptadas"
                : invitationStatusFilter === INVITATION_STATUS.REJECTED
                ? "Rechazadas"
                : "Expiradas"
            })`}
        </div>
      </section>

      {/* Lead Dialog */}
      <Dialog
        open={isLeadDialogOpen}
        onClose={() => setIsLeadDialogOpen(false)}
      >
        <DialogTitle>{selectedLead ? "Editar Lead" : "Nuevo Lead"}</DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre
              </label>
              <input
                type="text"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={leadForm.name}
                onChange={(e) =>
                  setLeadForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Empresa
              </label>
              <input
                type="text"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={leadForm.company}
                onChange={(e) =>
                  setLeadForm((prev) => ({ ...prev, company: e.target.value }))
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={leadForm.email}
                onChange={(e) =>
                  setLeadForm((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono
              </label>
              <input
                type="tel"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={leadForm.phone}
                onChange={(e) =>
                  setLeadForm((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={leadForm.status}
                onChange={(e) =>
                  setLeadForm((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <option value={LEAD_STATUS.NEW}>Nuevo</option>
                <option value={LEAD_STATUS.CONTACTED}>Contactado</option>
                <option value={LEAD_STATUS.NEGOTIATING}>En Negociación</option>
                <option value={LEAD_STATUS.CONVERTED}>Convertido</option>
                <option value={LEAD_STATUS.LOST}>Perdido</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas
              </label>
              <DialogTextarea
                value={leadForm.notes}
                onChange={(e) =>
                  setLeadForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                rows={4}
              />
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button outline onClick={() => setIsLeadDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={selectedLead ? handleUpdateLead : handleCreateLead}>
            {selectedLead ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invitation Dialog */}
      <Dialog
        open={isInvitationDialogOpen}
        onClose={() => setIsInvitationDialogOpen(false)}
      >
        <DialogTitle>
          {selectedInvitation ? "Editar Invitación" : "Nueva Invitación"}
        </DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre del Venue
              </label>
              <input
                type="text"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={invitationForm.venueName}
                onChange={(e) =>
                  setInvitationForm((prev) => ({
                    ...prev,
                    venueName: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre del Contacto
              </label>
              <input
                type="text"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={invitationForm.contactName}
                onChange={(e) =>
                  setInvitationForm((prev) => ({
                    ...prev,
                    contactName: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={invitationForm.email}
                onChange={(e) =>
                  setInvitationForm((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono
              </label>
              <input
                type="tel"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={invitationForm.phone}
                onChange={(e) =>
                  setInvitationForm((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
              />
            </div>

            {selectedInvitation && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={invitationForm.status}
                  onChange={(e) =>
                    setInvitationForm((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                >
                  <option value={INVITATION_STATUS.PENDING}>Pendiente</option>
                  <option value={INVITATION_STATUS.ACCEPTED}>Aceptada</option>
                  <option value={INVITATION_STATUS.REJECTED}>Rechazada</option>
                  <option value={INVITATION_STATUS.EXPIRED}>Expirada</option>
                </select>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button outline onClick={() => setIsInvitationDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={
              selectedInvitation
                ? handleUpdateInvitation
                : handleCreateInvitation
            }
          >
            {selectedInvitation ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Existing Venue Edit Dialog */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>Editar Venue: {selectedVenue?.name}</DialogTitle>
        <DialogDescription>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={editForm.isActive}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    isActive: e.target.value === "true",
                  }))
                }
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado Admin
              </label>
              <select
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={editForm.isActiveAdmin}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    isActiveAdmin: e.target.value === "true",
                  }))
                }
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado de Pago
              </label>
              <select
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={editForm.isPayed}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    isPayed: e.target.value === "true",
                  }))
                }
              >
                <option value="true">Pagado</option>
                <option value="false">Pendiente</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto
              </label>
              <input
                type="number"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={editForm.amount}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, amount: e.target.value }))
                }
                placeholder="Monto en MXN"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado de Onboardings
              </label>
              <select
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={editForm.onboardingStatus}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    onboardingStatus: e.target.value,
                  }))
                }
              >
                <option value={ONBOARDING_STATUS.NOT_STARTED}>
                  No Iniciado
                </option>
                <option value={ONBOARDING_STATUS.IN_PROGRESS}>
                  En Proceso
                </option>
                <option value={ONBOARDING_STATUS.DONE}>Completado</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cotizaciones Premium
              </label>
              <select
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={editForm.premiumCotizaciones}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    premiumCotizaciones: e.target.value === "true",
                  }))
                }
              >
                <option value="true">Habilitado</option>
                <option value="false">Deshabilitado</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Multiespacio (reservable por salón)
              </label>
              <select
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={editForm.multiespacio}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    multiespacio: e.target.value === "true",
                  }))
                }
              >
                <option value="true">Habilitado</option>
                <option value="false">Deshabilitado</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Deshabilitado, el venue puede crear espacios para mostrarlos en
                su ficha (cocina, jardín, salón principal) pero NO se reservan
                por separado: un evento sigue ocupando todo el venue.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bot de Telegram (beta)
              </label>
              <select
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={editForm.telegram}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    telegram: e.target.value === "true",
                  }))
                }
              >
                <option value="true">Habilitado</option>
                <option value="false">Deshabilitado</option>
              </select>
            </div>
          </div>
        </DialogDescription>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} outline>
            Cancelar
          </Button>
          <Button onClick={handleSaveChanges} disabled={updating}>
            {updating ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Home;
